import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type ConsentState = 'pending' | 'accepted' | 'rejected'

/**
 * Version of the cookie/privacy policy the stored consent was given against.
 *
 * BUMP THIS whenever the cookie or privacy policy changes materially - e.g. a new
 * vendor/tag is added, an existing one starts processing data for a new purpose, or
 * the retention/legal-basis wording changes. Cosmetic copy edits do not need a bump.
 *
 * Bumping does NOT by itself re-prompt anyone; it only makes `needsRenewal` true for
 * users whose stored consent predates the change (see the comment on `needsRenewal`).
 */
export const CONSENT_POLICY_VERSION = '1.1'

export const useConsentStore = defineStore('cookie-consent', () => {
  const state = ref<ConsentState>('pending')

  /** Epoch ms of the moment the current choice was recorded; null while pending. */
  const timestamp = ref<number | null>(null)

  /** Policy version the current choice was given against; null while pending. */
  const policyVersion = ref<string | null>(null)

  /**
   * GDPR Art. 7(1): the controller must be able to demonstrate that consent was given.
   * A recorded rejection matters just as much - it is the evidence for why no tags ran.
   */
  function record(next: ConsentState) {
    state.value = next
    timestamp.value = Date.now()
    policyVersion.value = CONSENT_POLICY_VERSION
  }

  function accept() {
    record('accepted')
  }

  function reject() {
    record('rejected')
  }

  /**
   * Full revocation: wipes the decision and its provenance so the user is treated as a
   * first-time visitor again (the consent banner reappears because it keys off
   * `state === 'pending'`).
   */
  function revoke() {
    state.value = 'pending'
    timestamp.value = null
    policyVersion.value = null
  }

  /**
   * True when a decision exists but was given against a different (or unknown) policy
   * version - i.e. that consent can no longer be relied on for the current policy.
   *
   * Deliberately a read-only signal, NOT an automatic re-prompt. Auto-resetting to
   * `pending` on a mismatch would fire during store rehydration, before the GTM plugin's
   * `immediate` watcher has settled, and would silently drop a still-valid `accepted`
   * state for every legacy visitor whose stored consent has no version at all. Consume
   * this from a UI surface when a policy change actually ships.
   */
  const needsRenewal = computed(
    () => state.value !== 'pending' && policyVersion.value !== CONSENT_POLICY_VERSION,
  )

  return { state, timestamp, policyVersion, accept, reject, revoke, needsRenewal }
}, {
  persist: {
    // Consent stored before this feature shipped has no timestamp/policyVersion. The
    // persistence plugin merges by key, so those refs keep their `null` defaults, but
    // normalise explicitly so a hand-edited or partially-written payload can never leak
    // `undefined` into a template or a date format.
    afterHydrate: (ctx) => {
      const store = ctx.store as unknown as {
        state: ConsentState
        timestamp: number | null
        policyVersion: string | null
      }
      if (store.state !== 'accepted' && store.state !== 'rejected') store.state = 'pending'
      if (typeof store.timestamp !== 'number') store.timestamp = null
      if (typeof store.policyVersion !== 'string') store.policyVersion = null
    },
  },
})
